import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

import { IForgotPasswordLiveParams } from '@aqx-core/common/api/auth';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { forgotPasswordViewController } from '@aqx-core/common/services/forgot-password';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import { getEnterValidText, isValidEmail } from '@aqx-core/common/validators';

const getScheme = (isCRM: boolean) => {
  const metatraderId = isCRM
    ? yup.string().optional()
    : yup.string().required(getEnterValidText({ label: 'account ID' }));

  return yup.object().shape({
    email: yup
      .string()
      .required(getEnterValidText({ label: 'email' }))
      .test('is-valid-email', getEnterValidText({ label: 'email' }), (value) => (value ? isValidEmail(value) : false)),
    metatraderId
  });
};

export const useForgotPasswordForm = (isCRM: boolean) => {
  const schema = useMemo(() => getScheme(isCRM), [isCRM]);

  const resolver = useMemo(() => yupResolver(schema) as Resolver<IForgotPasswordLiveParams>, [schema]);

  const useFormMethods = useBaseForm<IForgotPasswordLiveParams>({
    resolver,
    mode: 'onChange',
    shouldFocusError: false
  });

  const { handleSubmit, trigger, watch, formState } = useFormMethods;

  const [email, metatraderId] = watch(['email', 'metatraderId']);

  const {
    errors: { email: emailError, metatraderId: metatraderIdError }
  } = formState;

  const isDisabled = useMemo(() => {
    const isEmailValid = !!email && !emailError;
    const ismetatraderIdValid = !!metatraderId && !metatraderIdError;

    return isCRM ? !isEmailValid : !(isEmailValid && ismetatraderIdValid);
  }, [metatraderId, metatraderIdError, email, emailError, isCRM]);

  const onError = useCallback((message: string) => {
    snackbarController.showAlert({
      message,
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  const handleFormSubmit: SubmitHandler<IForgotPasswordLiveParams> = useCallback(
    (values) => {
      const { metatraderId, email } = values;
      const params = {
        email,
        metatraderId: isCRM ? undefined : metatraderId
      };
      forgotPasswordViewController.forgotPassword({ params, onError });
    },
    [isCRM, onError]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  return {
    useFormMethods,
    submit,
    trigger,
    isDisabled,
    email,
    metatraderId
  };
};
